import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputType } from '@vinnova/ui';
import {
  endOfYear,
  format,
  isAfter,
  isBefore,
  isSameSecond,
  setYear,
  startOfYear,
} from 'date-fns';
import {
  filter,
  Observable,
  of,
  skip,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { FormQuestionDate } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import { QuestionStructureDatepicker } from '../../../core/models/question/question-datepicker.model';
import { Question } from '../../../core/models/question/question.model';
import { StructuredDataKey } from '../../../core/models/question/structured-data-key.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { DatepickerInterval } from './validators/datepicker-interval.validator';
import { PreviousAnswersService } from '../../../core/services/previous-answers.service';

const DATE_FORMAT = 'yyyy-MM-dd';

@UntilDestroy()
@Component({
  selector: 'lib-question-datepicker',
  templateUrl: './question-datepicker.component.html',
  styleUrls: ['./question-datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionDatepickerComponent implements OnInit, OnChanges {
  value: FormControl<string>;
  fromDate: FormControl<string>;
  toDate: FormControl<string>;
  maxDate: Date;
  minDate: Date;
  maxEndDate: Date;
  minEndDate: Date;
  isOverMaxLength: boolean;
  InputType = InputType;
  questionNumber$: Observable<unknown>;
  readOnly$: Observable<ReadonlyState>;
  activeLang$: Observable<string>;
  errorMessage$: Observable<string>;
  errorMessageFrom$: Observable<string>;
  errorMessageTo$: Observable<string>;
  fromDateReadonly: WritableSignal<string> = signal(undefined);
  toDateReadonly: WritableSignal<string> = signal(undefined);
  ansokanCreatedDate: Date;
  maxDateIsFromFormSettings: boolean;
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  showChanges = input<boolean>();
  readonly previousAnswersService = inject(PreviousAnswersService);

  public StructuredDataKey = StructuredDataKey;

  @Input() questionsInRow: number;
  @Input() question: Question;
  @Input() labelPrefix: string;
  @Input() repeaterGroupId: string;

  _questionForm: FormGroup<ControlsOf<FormQuestionDate>>;
  @Input()
  set questionForm(form: FormGroup<ControlsOf<FormQuestionDate>>) {
    this._questionForm = form;
    this.value = this._questionForm.get('value');
    this.fromDate = this._questionForm.get('fromDate');
    this.toDate = this._questionForm.get('toDate');
    this.value.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  get questionForm() {
    return this._questionForm;
  }

  get structure() {
    return this.question.structure as QuestionStructureDatepicker;
  }

  constructor(
    private formValidationService: FormValidationService,
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private translocoService: TranslocoService,
    private questionNumberService: QuestionNumberService,
    private formsQuery: RendererFormsQuery,
  ) {}

  ngOnInit() {
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;

    // populate initial answers from the db
    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.value,
      this.question,
    );
    this.ansokanCreatedDate = this.questionValueChangeService.getCreatedDate();
    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question.id,
    );

    if (this.structure.interval) {
      this.value.addValidators(DatepickerInterval);
      this.value.updateValueAndValidity();
      if (this.question.validation.required) {
        this.fromDate.addValidators([Validators.required]);
        this.toDate.addValidators([Validators.required]);
      }
      this.setIntervalDate();
    }
    this.limitDates();
    this.readOnly$.pipe(untilDestroyed(this)).subscribe((readonly) => {
      if (readonly) {
        this.setReadonlyDates();
      }
      this.formControlValueChanges(this.fromDate, true);
      this.formControlValueChanges(this.toDate, false);
    });
    this.setErrorMessage();
    this.isOverMaxLength = this.checkIfIsOverMaxLength(
      new Date(this.fromDate.value),
      true,
    );

    this.questionValueChangeService.sendInitialKeyValue(
      this.question,
      this.value,
      {
        questionId: this.question.id,
        repeaterGroupId: this.repeaterGroupId,
      },
      this.questionForm.valid,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question, this.repeaterGroupId, this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => this.setReadonlyDates());

      if (
        this.formsQuery.getReadOnly() &&
        !changes['repeaterGroupId'].firstChange
      ) {
        this.setReadonlyDates();
      }
    }
  }

  private setIntervalDate() {
    const parsed = JSON.parse(this.value?.value);
    this.toDate.patchValue(parsed?.toDate);
    this.fromDate.patchValue(parsed?.fromDate);
    return parsed;
  }

  private setReadonlyDates() {
    if (this.structure.interval) {
      this.setIntervalDate();

      this.toDateReadonly.set(
        this.toDate.value ? format(this.toDate.value, DATE_FORMAT) : undefined,
      );
      this.fromDateReadonly.set(
        this.fromDate.value
          ? format(this.fromDate.value, DATE_FORMAT)
          : undefined,
      );
    } else {
      this.toDateReadonly.set(
        this.value.value ? format(this.value.value, DATE_FORMAT) : undefined,
      );
    }
  }

  setErrorMessage(): void {
    if (this.structure.interval) {
      this.errorMessageFrom$ = this.readOnly$.pipe(
        switchMap((readonly) => {
          switch (readonly) {
            case ReadonlyState.Editable: {
              return this.formValidationService
                .questionInvalid(this.fromDate, this.question.id)
                .pipe(
                  switchMap((invalid) => {
                    return invalid
                      ? this.getErrorMessage(this.fromDate, false, true)
                      : of(undefined);
                  }),
                );
            }
            case ReadonlyState.Locked: {
              return this.getErrorMessage(this.fromDate, true, true);
            }
            case ReadonlyState.Reader: {
              return of(undefined);
            }
          }
        }),
      );

      this.errorMessageTo$ = this.readOnly$.pipe(
        switchMap((readonly) => {
          switch (readonly) {
            case ReadonlyState.Editable: {
              return this.formValidationService
                .questionInvalid(this.toDate, this.question.id)
                .pipe(
                  switchMap((invalid) => {
                    return invalid
                      ? this.getErrorMessage(this.toDate, false, false)
                      : of(undefined);
                  }),
                );
            }
            case ReadonlyState.Locked: {
              return this.getErrorMessage(this.toDate, true, false);
            }
            case ReadonlyState.Reader: {
              return of(undefined);
            }
          }
        }),
      );
    } else {
      this.errorMessage$ = this.readOnly$.pipe(
        switchMap((readonly) => {
          switch (readonly) {
            case ReadonlyState.Editable: {
              return this.formValidationService
                .questionInvalid(this.value, this.question.id)
                .pipe(
                  switchMap((invalid) => {
                    return invalid
                      ? this.getErrorMessage(this.value, false)
                      : of(undefined);
                  }),
                );
            }
            case ReadonlyState.Locked: {
              return this.getErrorMessage(this.value, true);
            }
            case ReadonlyState.Reader: {
              return of(undefined);
            }
          }
        }),
      );
    }
  }

  limitDates() {
    const earliestStartDateAllowed = this.structure.earliestDateAllowed
      ? new Date(this.structure.earliestDateAllowed)
      : undefined;
    const latestStartDateAllowed = this.structure.latestDateAllowed
      ? new Date(this.structure.latestDateAllowed)
      : undefined;
    const earliestEndDateAllowed = this.structure.earliestIntervalDateAllowed
      ? new Date(this.structure.earliestIntervalDateAllowed)
      : undefined;
    const latestEndDateAllowed = this.structure.latestIntervalDateAllowed
      ? new Date(this.structure.latestIntervalDateAllowed)
      : undefined;
    this.maxEndDate = latestEndDateAllowed;
    this.minDate = isBefore(earliestStartDateAllowed, new Date())
      ? new Date()
      : earliestStartDateAllowed;
    this.checkAndSetMaxDate(latestStartDateAllowed);

    this.maxEndDate =
      latestEndDateAllowed ||
      new Date(new Date().setFullYear(new Date().getFullYear() + 5, 11, 31));

    if (this.question.key === StructuredDataKey.Projekttid) {
      this.checkAndSetMinDate(earliestStartDateAllowed);
      if (this.toDate.value) {
        this.minDate = this.setYearLimit(false);
        this.checkAndSetMinDate(earliestStartDateAllowed);
        this.checkAndSetMaxDate(latestStartDateAllowed);
      }
    }
    this.minEndDate = earliestStartDateAllowed
      ? this.fromDate?.value &&
        isAfter(this.fromDate.value, earliestStartDateAllowed)
        ? new Date(this.fromDate.value)
        : new Date(earliestStartDateAllowed)
      : this.fromDate?.value &&
          !isAfter(this.fromDate.value, this.maxDate) &&
          !isBefore(this.fromDate.value, this.minDate)
        ? isAfter(this.fromDate.value, earliestEndDateAllowed) ||
          !earliestEndDateAllowed
          ? new Date(this.fromDate.value)
          : (earliestEndDateAllowed ?? new Date())
        : (earliestEndDateAllowed ?? new Date());

    this.maxDateIsFromFormSettings = isSameSecond(
      this.maxEndDate,
      latestEndDateAllowed,
    );
  }

  checkAndSetMaxDate(latestStartDateAllowed: Date) {
    if (!latestStartDateAllowed) {
      latestStartDateAllowed = new Date(
        new Date().setFullYear(new Date().getFullYear() + 5, 11, 31),
      );
    }
    if (this.toDate.value) {
      this.maxDate =
        isBefore(this.toDate.value, latestStartDateAllowed) &&
        isAfter(this.toDate.value, this.minDate)
          ? new Date(this.toDate.value)
          : latestStartDateAllowed;
    } else {
      this.maxDate = latestStartDateAllowed;
    }
  }

  checkAndSetMinDate(earliestStartDateAllowed: Date) {
    if (earliestStartDateAllowed) {
      if (this.minDate.getFullYear() < earliestStartDateAllowed.getFullYear()) {
        this.minDate = new Date(earliestStartDateAllowed);
      }
    } else if (this.questionValueChangeService.getCreatedDate()) {
      this.minDate =
        isBefore(
          this.questionValueChangeService.getCreatedDate(),
          this.minDate,
        ) && this.minDate
          ? this.minDate
          : this.questionValueChangeService.getCreatedDate();
    } else {
      this.minDate = new Date();
      if (this.minEndDate === undefined || this.minEndDate < new Date()) {
        this.minEndDate = new Date();
      }
    }
  }

  setYearLimit(fromDate: boolean, latestEndDateAllowed?: Date) {
    const newMaxDate = endOfYear(
      setYear(this.fromDate.value, this.maxEndDate?.getFullYear()),
    );
    return fromDate
      ? latestEndDateAllowed
        ? isBefore(newMaxDate, latestEndDateAllowed)
          ? new Date(newMaxDate)
          : latestEndDateAllowed
        : new Date(newMaxDate)
      : startOfYear(
          setYear(this.toDate.value, this.maxDate?.getFullYear() - 5),
        );
  }
  formControlValueChanges(
    formControl: FormControl<string>,
    fromDateControl: boolean,
  ) {
    formControl.valueChanges
      .pipe(
        skip(1),
        untilDestroyed(this),
        filter(() => !this.formsQuery.getReadOnly()),
      )
      .subscribe(() => {
        const isOverMaxLength = this.checkIfIsOverMaxLength(
          new Date(formControl.value),
          fromDateControl,
        );
        this.isOverMaxLength = isOverMaxLength;
        if (isOverMaxLength) {
          const existingErrors = formControl.errors;
          const newError = { isOverMaxLength: true };
          formControl.setErrors({ ...existingErrors, ...newError });
        } else if (this.isOverMaxLength) {
          this.isOverMaxLength = false;
          if (fromDateControl) {
            this.toDate.setErrors(undefined);
          } else {
            this.fromDate.setErrors(undefined);
          }
        }

        if (
          (this.fromDate.errors || this.toDate.errors) &&
          (this.toDate.value >= this.fromDate.value ||
            this.fromDate.value <= this.toDate.value)
        ) {
          if (
            this.fromDate.errors?.invalidDate &&
            !this.fromDate.errors?.isEarlyYear &&
            (!this.fromDate.errors?.isLateYear ||
              (format(this.toDate.value, 'yyyy-MM-dd') >= this.fromDate.value &&
                (this.structure.latestDateAllowed === '' ||
                  this.structure.latestDateAllowed === undefined)) ||
              new Date(this.fromDate.value) <=
                new Date(this.structure.latestDateAllowed))
          ) {
            this.fromDate.setErrors(undefined);
          } else if (
            this.toDate.errors?.invalidDate &&
            (!this.toDate.errors?.isEarlyYear ||
              ((this.structure.earliestIntervalDateAllowed === '' ||
                this.structure.latestDateAllowed === undefined) &&
                format(this.toDate.value, 'yyyy-MM-dd') >=
                  this.fromDate.value) ||
              new Date(this.toDate.value) >=
                new Date(this.structure.earliestIntervalDateAllowed)) &&
            !this.toDate.errors?.isLateYear
          ) {
            this.toDate.setErrors(undefined);
          }
        }
        this.setErrorMessage();

        this.value.patchValue(
          !this.fromDate.value && !this.toDate.value
            ? JSON.stringify({
                fromDate: undefined,
                toDate: undefined,
              })
            : JSON.stringify({
                fromDate: this.fromDate.value
                  ? format(this.fromDate.value, DATE_FORMAT)
                  : undefined,
                toDate: this.toDate.value
                  ? format(this.toDate.value, DATE_FORMAT)
                  : undefined,
              }),
        );
        this.value.markAsDirty();
        this.limitDates();
        this.cdr.detectChanges();
      });
  }

  private checkIfIsOverMaxLength(date: Date, isStartDate: boolean) {
    if (this.question.key !== StructuredDataKey.Projekttid) return false;
    return isStartDate
      ? date.getFullYear() + 5 < new Date(this.toDate.value)?.getFullYear()
      : date.getFullYear() - 5 > new Date(this.fromDate.value)?.getFullYear();
  }

  private setInvalidDateMessage(
    isIntervalStartDate: boolean,
    isEarlyYear: boolean,
    isLateYear: boolean,
    isOverMaxLength: boolean,
  ): Observable<string> {
    if (isEarlyYear) {
      return this.translocoService.selectTranslate(
        'datepicker_validation_endDate_inRange',
        {
          startDate: format(
            isIntervalStartDate ? this.minDate : this.minEndDate,
            DATE_FORMAT,
          ),
        },
      );
    } else if (isLateYear) {
      return this.translocoService.selectTranslate(
        'datepicker_validation_startDate_inRange',
        {
          endDate: format(
            isIntervalStartDate ? this.maxDate : this.maxEndDate,
            DATE_FORMAT,
          ),
        },
      );
    } else if (isOverMaxLength) {
      return this.translocoService.selectTranslate(
        'datepicker_validation_to_many_years_ahead',
      );
    }
    return of(undefined);
  }
  getErrorMessage(
    control: FormControl<string>,
    readonly?: boolean,
    isIntervalStartDate?: boolean,
  ): Observable<string> {
    if (control.errors?.required && !control.errors?.invalidDate) {
      return readonly
        ? this.translocoService.selectTranslate('field_required')
        : this.translocoService.selectTranslate('required');
    }
    if (control.errors?.invalidDate) {
      return this.setInvalidDateMessage(
        isIntervalStartDate,
        control.errors.isEarlyYear,
        control.errors.isLateYear,
        control.errors.isOverMaxLength ?? this.isOverMaxLength,
      );
    } else if (this.isOverMaxLength) {
      return this.overMaxLengthErrorText(
        control.errors.isOverMaxLength ?? this.isOverMaxLength,
        isIntervalStartDate,
        true,
      );
    }
    return of(undefined);
  }

  private overMaxLengthErrorText(
    _isOverMaxLength: boolean,
    isIntervalStartDate: boolean,
    isProjekttid: boolean,
  ) {
    if (this.isOverMaxLength && isProjekttid) {
      return this.translocoService.selectTranslate(
        'datepicker_validation_to_many_years_ahead',
      );
    } else if (isIntervalStartDate) {
      return this.translocoService.selectTranslate(
        'datepicker_validation_endDate_inRange',
        {
          startDate: format(this.minDate, DATE_FORMAT),
        },
      );
    } else {
      return this.structure.interval
        ? this.translocoService.selectTranslate(
            'datepicker_validation_startDate_inRange',
            {
              endDate: format(this.maxEndDate, DATE_FORMAT),
            },
          )
        : this.translocoService.selectTranslate(
            'datepicker_validation_startDate_inRange',
            {
              endDate: format(this.maxDate, DATE_FORMAT),
            },
          );
    }
  }
}
